// ============================================================
// PythonAssignment.jsx
// ============================================================
// Junior-dev Notes:
// - Logic: Focuses on PEP 8 standards, List Comprehensions, and Async.
// - UI: Goldenrod accents (#E5C95F) on Neural Purple (#100720).
// - Strategy: 30 Challenges for Data Science & Backend Mastery.
// - Branding: prodevconsult.com | Finnish Engineering Standards
// ============================================================

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    Chip,
} from "@mui/material";

/* Icons */
import { AiFillBulb } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import TerminalIcon from "@mui/icons-material/Terminal";
import PsychologyIcon from "@mui/icons-material/Psychology"; // For AI/Logic feel
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5";

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.92), rgba(16,7,32,0.96)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(255,255,255,0.03)",
    borderRadius: "16px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
    border: "1px solid rgba(229, 201, 95, 0.15)",
    color: "white",
    transition: "0.3s ease",
    "&:hover": {
        border: "1px solid rgba(229, 201, 95, 0.4)",
        backgroundColor: "rgba(255,255,255,0.05)",
    }
}));

const QuestionItem = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    "&:last-child": { borderBottom: "none" },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#E5C95F",
    color: "#100720",
    fontWeight: 800,
    padding: "14px 45px",
    borderRadius: "4px",
    "&:hover": {
        backgroundColor: "#FFF0A5",
        transform: "translateY(-3px)",
    },
}));

/* ── Main Component ──────────────────────────────────────── */

export default function PythonAssignment() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleCopy = (questions, index) => {
        const text = questions.join("\n");
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const assignmentData = useMemo(() => [
        {
            title: "Syntax & Data Structures",
            questions: [
                "Swap two variables without using a third temporary variable.",
                "Implement a function that removes duplicates from a list while maintaining order.",
                "Use Slicing to reverse a string and check if it is a palindrome.",
                "Create a Dictionary comprehension that maps numbers 1-10 to their squares.",
                "Demonstrate the difference between 'is' and '==' with mutable objects."
            ]
        },
        {
            title: "Functional Python (Pythonic Way)",
            questions: [
                "Use List Comprehension to extract all prime numbers from a given range.",
                "Write a Lambda function inside a filter() to find strings longer than 5 chars.",
                "Implement a generator function that yields the Fibonacci sequence (Memory Efficient).",
                "Use the zip() function to merge two lists into a dictionary.",
                "Demonstrate 'map' vs 'list comprehension' for a mathematical transformation."
            ]
        },
        {
            title: "Object-Oriented Python (OOP)",
            questions: [
                "Create a 'Bank' class using __init__ and private attributes (name mangling).",
                "Simulate 'Inheritance' by creating a Base 'Employee' and Child 'Manager' class.",
                "Implement Method Overriding and use the super() function.",
                "Define a custom __str__ method to provide a readable object representation.",
                "Build a simple library system using a list of 'Book' objects inside a 'Library' class."
            ]
        },
        {
            title: "Advanced Concepts & Decorators",
            questions: [
                "Create a '@timer' decorator that measures the execution time of any function.",
                "Implement a context manager using the 'with' statement for file handling.",
                "Write a closure function that maintains a running total of added values.",
                "Demonstrate Arg and Kwargs by building a flexible logging function.",
                "Handle multi-level exceptions (try-except-finally) for a network simulation."
            ]
        },
        {
            title: "File I/O & Automation",
            questions: [
                "Write a script that searches for a keyword in 10 different .txt files.",
                "Parse a JSON file and convert it into a CSV format using the 'csv' module.",
                "Implement a simple web scraper using 'requests' and 'BeautifulSoup'.",
                "Automate the renaming of all files in a folder based on their creation date.",
                "Read a large log file line-by-line without loading the entire file into RAM."
            ]
        },
        {
            title: "Asynchronous & AI Logic",
            questions: [
                "Use 'asyncio' to run three simulated API calls concurrently.",
                "Implement a simple Linear Regression logic using basic Python math (No libraries).",
                "Build a 'chatbot' logic using nested if-elif or a match-case statement (Python 3.10+).",
                "Algorithm to find the 'Missing Number' in a list of 1 to N using the Sum formula.",
                "Use the 'threading' module to run a background task while the main loop continues."
            ]
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 8, px: 2 }}>
                <Typography variant={isMobile ? "h4" : "h2"} sx={{ fontWeight: 900, color: "#E5C95F", mb: 1 }}>
                    PYTHON MASTER CHALLENGE
                </Typography>
                <Chip
                    label="PRODEV CONSULT · FINNISH ENGINEERING STANDARDS"
                    sx={{ bgcolor: "rgba(229, 201, 95, 0.1)", color: "#E5C95F", fontWeight: 800, border: "1px solid #E5C95F" }}
                />
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                            <PsychologyIcon sx={{ fontSize: 40, color: "#E5C95F" }} />
                            <Typography variant="h5" fontWeight={800} color="#E5C95F">AI & Backend Strategy</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.8, mb: 4, lineHeight: 1.8 }}>
                            Python is the language of data and automation. This master assignment challenges you to move beyond basic scripts and master **Asynchronous I/O**, **Object-Oriented Design**, and **Clean Pythonic Logic**.
                        </Typography>

                        <Grid container spacing={2}>
                            {[
                                "Master List & Dict Comprehensions",
                                "Asynchronous Programming (asyncio)",
                                "Decorators & Context Managers",
                                "Memory-Efficient Generators",
                                "Scalable Backend Architectures"
                            ].map((item, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <IntegrationInstructionsIcon sx={{ color: "#00ED64", fontSize: 18 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{item}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>

                {assignmentData.map((topic, index) => (
                    <Grid item xs={12} key={index}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(229, 201, 95, 0.15)", color: "#E5C95F", border: "1px solid #E5C95F" }}>
                                        <TerminalIcon size={22} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={800}>{topic.title}</Typography>
                                </Box>

                                <Stack direction="row" spacing={1.5}>
                                    <Tooltip title={copiedIndex === index ? "Copied!" : "Copy Tasks"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(topic.questions, index)}
                                            sx={{ color: "white", borderColor: "rgba(255,255,255,0.2)" }}
                                        >
                                            {copiedIndex === index ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<CodeIcon />}
                                            sx={{ bgcolor: "#E5C95F", color: "#100720", fontWeight: 700 }}
                                        >
                                            Python IDE
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                {topic.questions.map((q, i) => (
                                    <QuestionItem key={i}>
                                        <Typography sx={{ color: "#E5C95F", fontWeight: 900, minWidth: "25px" }}>{i + 1}.</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.85 }}>{q}</Typography>
                                    </QuestionItem>
                                ))}
                            </Box>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center" sx={{ mt: 6, mb: 10 }}>
                    <Stack direction={isMobile ? "column" : "row"} spacing={3} justifyContent="center" alignItems="center">
                        <Link to="/pythonCodingDashBoard" style={{ textDecoration: "none" }}>
                            <ActionButton variant="contained">
                                BACK TO DASHBOARD
                            </ActionButton>
                        </Link>
                    </Stack>
                </Grid>
            </Grid>

            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    sx={{ position: "fixed", bottom: 30, right: 30, bgcolor: "#E5C95F" }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
